import {
  Button,
  Col,
  Row,
  Form,
  Input,
  message,
  InputNumber,
  Radio,
  Table,
  Space,
  Modal,
  Typography,
  Upload,
} from "antd";
import {
  EyeOutlined,
  DeleteOutlined,
  InboxOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API, Auth } from "aws-amplify";
import SkeletonBlock from "utility/SkeletonBlocks";
import {
  cityPattern,
  formatNumberUS,
  streetAddressPattern,
  isUuid,
  tenDigit,
  zipPattern,
  urlPattern,
  doctorNameRegex,
} from "utility/constant";
import SuccessModel from "utility/SuccessModel";
import { getEpcEmployer } from "graphql/customQuery";
import { updateEpcEmployerProfile } from "graphql/mutations";
import { uploadFileToS3 } from "utility/uploadFileToS3";
import { CopyToClipboard as OriginalCopyToClipboard } from "react-copy-to-clipboard";
import { updateEpcEmployer } from "graphql/customEpcMutation";
import DeleteModal from "utility/DeleteModal";
import type { RcFile, UploadProps } from "antd/es/upload";
import type { FormInstance } from "antd/es/form";

interface DocumentData {
  name?: string;
  description?: string;
  key?: string;
  fileBlob?: File;
}

interface EmployerProfileData {
  id?: string;
  profile?: {
    name?: string;
    phone?: string;
    website?: string;
    form?: {
      formStatus?: string;
      formLink?: string;
    };
    email?: string;
    note?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zip?: string;
    documents?: DocumentData[];
  };
  name?: string;
  searchByName?: string;
}

export default function EmployerProfile() {
  const { Dragger } = Upload;
  const { id: employerID } = useParams<{ id: string }>();
  const [cognitoId, setCognitoId] = useState<string | null>(null);
  const [profileID, setProfileID] = useState<string | null>(null);
  const [skeletonLoader, setskeletonLoader] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [document, setDocument] = useState<DocumentData[]>([]);
  const [employersData, setEmployerData] = useState<EmployerProfileData>({});
  const [successModel, setSuccessModel] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [formLink, setFormLink] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [documentToUpload, setDocumentToUpload] = useState<DocumentData[]>([]);
  const [form] = Form.useForm();
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const navigate = useNavigate();

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const uploadFormRef = useRef<FormInstance | null>(null);
  const deleteDocumentID = useRef<{ key?: string; fileBlob?: File } | null>(
    null
  );
  const successMessage = useRef<string | null>(null);

  const handleValuesChange = () => {
    setSaveDisabled(false);
  };

  const CopyToClipboard = OriginalCopyToClipboard as unknown as React.FC<{
    text: string;
    onCopy: () => void;
    children: React.ReactNode;
  }>;

  const fetchEmployerProfile = async () => {
    if (!employerID || !isUuid.test(employerID)) {
      navigate("/404", {
        state: { message: `No data found for Employer ${employerID}` },
      });
      return;
    }
    setskeletonLoader(true);
    try {
      const response = (await API.graphql({
        query: getEpcEmployer,
        variables: {
          id: employerID,
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      })) as any;
      if (!response?.data) {
        navigate("/404", {
          state: { message: `No data found for Employer ${employerID}` },
        });
        return;
      }
      setProfileID(response.data?.getEpcEmployer?.profileId);
      setEmployerData(response.data?.getEpcEmployer);
      setDocument(response.data?.getEpcEmployer?.profile?.documents || []);
      setDocumentToUpload([]);
      setFormLink(
        response.data?.getEpcEmployer?.profile?.form?.formLink ||
          `${window.location.origin}/enhanced-primary-care/employer/${employerID}`
      );
    } finally {
      setskeletonLoader(false);
    }
  };

  useEffect(() => {
    fetchEmployerProfile();
    // eslint-disable-next-line
  }, [employerID]);

  const initialValues = {
    name: employersData?.profile?.name,
    phoneNumber: employersData?.profile?.phone,
    website: employersData?.profile?.website,
    formStatus: employersData?.profile?.form?.formStatus || "ON",
    formLink: formLink,
    note: employersData?.profile?.note,
    email: employersData?.profile?.email,
    streetAddress: employersData?.profile?.streetAddress,
    city: employersData?.profile?.city,
    state: employersData?.profile?.state,
    zip: employersData?.profile?.zip,
    employerID: employersData?.id,
  };

  const updateEmployerProfile = async (values: any) => {
    try {
      setUpdateLoading(true);
      const validDocuments = document.filter((doc) => doc.key);
      let uploadedDocuments: DocumentData[] = [];
      if (documentToUpload.length > 0) {
        messageApi.loading("Uploading files, please wait...");
        uploadedDocuments = await Promise.all(
          documentToUpload.map(async (file) => {
            const uploadResult = await uploadFileToS3(file?.fileBlob as File);
            let loggedInUser = null;
            if (!cognitoId) {
              loggedInUser = await Auth.currentUserInfo();
              setCognitoId(loggedInUser?.id);
            }
            const identityId = cognitoId || loggedInUser?.id;
            return {
              name: file.name || "Untitled",
              description: file.description || "-",
              key: `private/${identityId}/${uploadResult.key}`,
            };
          })
        );
        messageApi.success("Files uploaded successfully!");
      }
      const updatedDocuments = [...validDocuments, ...uploadedDocuments];
      const inputData = {
        input: {
          id: profileID,
          name: values?.name,
          phone: values?.phoneNumber,
          streetAddress: values?.streetAddress,
          city: values?.city,
          state: values?.state,
          note: values?.note || "",
          zip: values?.zip,
          website: values?.website,
          form: {
            formStatus: values?.formStatus || "ON",
          },
          documents: updatedDocuments,
        },
      };
      await API.graphql({
        query: updateEpcEmployerProfile,
        variables: inputData,
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (employersData?.searchByName !== values?.name?.toLowerCase()) {
        await API.graphql({
          query: updateEpcEmployer,
          variables: {
            input: {
              id: employerID,
              searchByName: values?.name?.toLowerCase(),
            },
          },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
      }
      setSuccessModel(true);
      setSaveDisabled(true);
      fetchEmployerProfile();
      successMessage.current = "Employer Details Updated Successfully";
      setTimeout(() => {
        setSuccessModel(false);
        setTimeout(() => {
          successMessage.current = null;
        }, 1000);
      }, 3000);
    } catch (error: any) {
      console.error("Error updating employer profile:", error);
      messageApi.error(error.message || "Error updating data");
    } finally {
      setUpdateLoading(false);
    }
  };

  const deleteDocument = async () => {
    try {
      setDeleteLoading(true);
      if (
        deleteDocumentID.current?.key === undefined &&
        deleteDocumentID.current?.fileBlob
      ) {
        const updatedDocumentList = document.filter(
          (obj) => obj?.fileBlob !== deleteDocumentID.current?.fileBlob
        );
        setDocument(updatedDocumentList);
        const updatedDocumentToUploadList = documentToUpload.filter(
          (file) => file?.fileBlob !== deleteDocumentID.current?.fileBlob
        );
        setDocumentToUpload(updatedDocumentToUploadList);
      } else {
        let url = `/epc/superadmin/moveToTrash`;
        messageApi.loading("Deleting document from the cloud ☁️");
        const requestBody = {
          body: {
            key: deleteDocumentID.current?.key,
          },
        };
        await API.post("bazarhealthREST", url, requestBody);

        const updatedDocumentList = document.filter(
          (obj) => obj?.key !== deleteDocumentID.current?.key
        );
        setDocument(updatedDocumentList);
        const inputData = {
          input: {
            id: profileID,
            documents: updatedDocumentList,
          },
        };
        await API.graphql({
          query: updateEpcEmployerProfile,
          variables: inputData,
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
      }
      successMessage.current = "document deleted successfully!";
      setSuccessModel(true);
      setTimeout(() => {
        setSuccessModel(false);
        setTimeout(() => {
          successMessage.current = null;
        }, 1000);
      }, 3000);
    } catch (error) {
      console.log(error);
      messageApi.error("Something went wrong, deleting the document");
    } finally {
      setDeleteLoading(false);
      setDeleteModal(false);
    }
  };

  const documentColoumn = [
    {
      title: "File",
      dataIndex: "name",
      ellipsis: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
    },
    {
      title: "Action",
      dataIndex: "Action",
      className: "actions",
      render: (_: any, value: DocumentData) => {
        return (
          <Space size={20}>
            <EyeOutlined
              style={{ cursor: "pointer" }}
              onClick={async () => {
                if (value?.key === undefined) {
                  const url = URL.createObjectURL(value?.fileBlob as File);
                  window.open(url, "_blank");
                  URL.revokeObjectURL(url);
                } else {
                  let url = `/epc/superadmin/sendPresignedUrlEpc`;
                  messageApi.loading("Fetching document from the cloud ☁️");
                  const requestBody = {
                    body: {
                      key: value.key,
                    },
                  };
                  let result = await API.post(
                    "bazarhealthREST",
                    url,
                    requestBody
                  );
                  window.open(result.url, "_blank");
                }
              }}
            />
            <DeleteOutlined
              style={{ cursor: "pointer" }}
              onClick={() => {
                setDeleteModal(true);
                deleteDocumentID.current = value?.key
                  ? { key: value?.key }
                  : { fileBlob: value?.fileBlob };
              }}
            />
          </Space>
        );
      },
    },
  ];

  const props: UploadProps = {
    beforeUpload: (file: RcFile) => {
      const maxFileSize = 25 * 1024 * 1024; // 25 MB limit
      if (file.size > maxFileSize) {
        message.error(`${file.name} file upload failed (exceeds 25MB)`);
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange(info) {
      if (info.fileList.length === 0 && uploadFormRef.current) {
        // @ts-ignore
        uploadFormRef.current.document = {};
      }
    },
    async customRequest({ file, onError, onSuccess }) {
      setTimeout(() => {
        if (onSuccess) onSuccess("ok");
      }, 0);
    },
  };

  const uploadDocument = (values: any) => {
    if (
      !values?.document?.fileList ||
      values?.document?.fileList.length === 0
    ) {
      messageApi.warning("Please upload a document");
      return;
    }
    setDocumentToUpload((items) => [
      {
        fileBlob: values?.document?.file?.originFileObj,
        name: values?.name,
        description: values?.description || "-",
      },
      ...items,
    ]);
    setDocument((files) => [
      {
        name: values?.name,
        description: values?.description || "-",
        key: undefined,
        fileBlob: values?.document?.file?.originFileObj,
      },
      ...files,
    ]);
    setUploadModal(false);
    setSaveDisabled(false);
    form.resetFields();
  };

  return (
    <>
      {contextHolder}
      <Row justify="center" className="update-form">
        <Col span={16} md={16} xs={24} sm={24}>
          {skeletonLoader ? (
            <Row gutter={[20, 20]} className="mt-3">
              <SkeletonBlock style={{ width: 700, height: 50 }} />
              <SkeletonBlock style={{ width: 700, height: 50 }} />
              <SkeletonBlock style={{ width: 700, height: 50 }} />
              <SkeletonBlock style={{ width: 700, height: 50 }} />
              <SkeletonBlock style={{ width: 700, height: 50 }} />
              <SkeletonBlock style={{ width: 700, height: 50 }} />
              <SkeletonBlock style={{ width: 700, height: 200 }} />
            </Row>
          ) : (
            <>
              <Form
                name="basic"
                initialValues={initialValues}
                onFinish={updateEmployerProfile}
                autoComplete="off"
                layout="vertical"
                className="mt-30"
                onValuesChange={handleValuesChange}
              >
                <Row gutter={20}>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item
                      label="Employer name"
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter Employer Name",
                        },
                        {
                          pattern: doctorNameRegex,
                          message: "Please enter a valid Employer name",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item
                      label="Employer email id"
                      name="email"
                      rules={[
                        {
                          required: true,
                          message: "Please enter employer email id",
                        },
                      ]}
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item
                      label="Employer url"
                      name="website"
                      rules={[
                        {
                          required: true,
                          message: "Please enter employer wesbite url",
                        },
                        {
                          pattern: urlPattern,
                          message: "Please input valid url",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item
                      label="Employer phone number"
                      name="phoneNumber"
                      rules={[
                        {
                          required: true,
                          message: "Please enter employer phone number",
                        },
                        {
                          pattern: tenDigit,
                          message: "Please enter a valid phone number",
                        },
                      ]}
                    >
                      <InputNumber
                        controls={false}
                        formatter={(value) =>
                          formatNumberUS(value ? String(value) : "")
                        }
                        parser={(value) => {
                          const cleanedValue = (value ?? "").replace(/\D/g, "");
                          return cleanedValue.slice(0, 10);
                        }}
                        style={{ width: "100%", borderRadius: "5px" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item label="Form link" name="formLink">
                      <Input
                        disabled
                        suffix={
                          <CopyToClipboard
                            text={formLink || ""}
                            onCopy={() =>
                              message.success(`Link copied to clipboard`)
                            }
                          >
                            <CopyOutlined />
                          </CopyToClipboard>
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item label="Form Status" name="formStatus">
                      <Radio.Group>
                        <Radio value={"ON"}>On</Radio>
                        <Radio value={"OFF"}>Off</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item
                      label="Street address"
                      name="streetAddress"
                      rules={[
                        {
                          pattern: streetAddressPattern,
                          message: "Please enter a valid street address",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item
                      label="City"
                      name="city"
                      rules={[
                        {
                          pattern: cityPattern,
                          message: "Please enter a valid city name",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item
                      label="State"
                      name="state"
                      rules={[
                        {
                          pattern: cityPattern,
                          message: "Please enter a valid state name",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12} md={12} xs={24} sm={24}>
                    <Form.Item
                      label="Zip"
                      name="zip"
                      rules={[
                        {
                          pattern: zipPattern,
                          message: "Please enter a valid ZIP code",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={24} xs={24} sm={24}>
                    <Form.Item label="Note" name="note">
                      <Input.TextArea
                        rows={3}
                        placeholder="Write any notes here..."
                      />
                    </Form.Item>
                  </Col>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0 10px 0 10px",
                    }}
                  >
                    <span
                      style={{
                        color: "#646464",
                        fontWeight: 600,
                        lineHeight: "21px",
                        fontSize: "15px",
                      }}
                    >
                      Uploaded Documents{" "}
                    </span>
                    <Button
                      size="large"
                      className="theme-btn"
                      style={{
                        alignSelf: "center",
                        border: "1px solid #00274c",
                        background: "transparent",
                      }}
                      onClick={() => setUploadModal(true)}
                    >
                      <span
                        style={{
                          color: "#00274c",
                          lineHeight: "1px",
                          fontSize: "14px",
                        }}
                      >
                        Upload
                      </span>
                    </Button>
                  </div>
                  <Table
                    columns={documentColoumn}
                    className="common-table"
                    pagination={false}
                    scroll={{
                      y: 160,
                    }}
                    dataSource={document}
                    // rowKey={(record) => record.key || record.name}
                  />
                  <Col span={24} md={24} xs={24} sm={24} className="center-btn">
                    <Button
                      size="large"
                      className="theme-btn mt-20"
                      htmlType="submit"
                      loading={updateLoading}
                      disabled={saveDisabled}
                    >
                      Save
                    </Button>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </Col>
      </Row>
      <SuccessModel
        message={successMessage.current || "Task done successfully!"}
        open={successModel}
      />
      <Modal
        className="form-modal"
        centered
        footer={null}
        width={700}
        title={
          <Typography.Title level={4} className="modal-title">
            {`Upload document ${
              employersData?.name ? `for ${employersData?.name} ` : ""
            }`}
          </Typography.Title>
        }
        open={uploadModal}
        onCancel={() => {
          setUploadModal(false);
          form.resetFields();
        }}
      >
        <Form
          name="basic"
          onFinish={uploadDocument}
          ref={uploadFormRef as React.RefObject<FormInstance>}
          autoComplete="off"
          layout="vertical"
          form={form}
        >
          <Row gutter={20}>
            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="File name"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter file name",
                  },
                  {
                    pattern: /^\S.*\S$|^\S$/,
                    message:
                      "File name cannot be empty, spaces or with trailing spaces",
                  },
                ]}
              >
                <Input maxLength={20} showCount />
              </Form.Item>
            </Col>
            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Description"
                name="description"
                rules={[
                  {
                    required: false,
                    message: "Please enter employer email id",
                  },
                ]}
              >
                <Input maxLength={200} showCount />
              </Form.Item>
            </Col>
            <Col span={24} md={24} xs={24} sm={24}>
              <Form.Item
                label=""
                name="document"
                rules={[
                  {
                    required: true,
                    message: "Please upload a Document",
                  },
                ]}
              >
                <Dragger
                  {...props}
                  maxCount={1}
                  accept=".xlsx, .xls, .csv, .txt, .ods, .pdf, .docx, .doc, .txt, .rtf, .odt, .jpg, .jpeg, .png"
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag a Document to this area to upload
                  </p>
                  <p className="ant-upload-hint">
                    Uploading more than a document will replace the exisitng
                    one.
                  </p>
                </Dragger>
              </Form.Item>
            </Col>
            <Col span={24} md={24} xs={24} sm={24} className="center-btn">
              <Button size="large" className="theme-btn" htmlType="submit">
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
      <DeleteModal
        content="Are you sure you want to delete this document?"
        open={deleteModal}
        onClick={deleteDocument}
        buttonLoading={deleteLoading}
        onOk={() => {
          setDeleteModal(false);
          deleteDocumentID.current = null;
        }}
        onCancel={() => {
          setDeleteModal(false);
          deleteDocumentID.current = null;
        }}
        buttonText="Yes"
      />
    </>
  );
}
